import React, { forwardRef, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
	Box,
	Card,
	CardContent,
	Typography,
	Chip,
	Stack,
	Divider,
	CircularProgress,
	Button,
	Select,
	MenuItem,
	List,
	ListItem,
	ListItemText,
	Fade,
	styled,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from "@mui/material";
import PageContainer from "./PageContainer";
import { getMatchingResumes } from "../../services/matchingService";
import { updateApplicationStatus } from "../../services/applicationService";
import { getResumeById } from "../../services/resumeService";
import { useLocation } from "react-router-dom";

const STATUS_COLORS = {
	APPLIED: "info",
	UNDER_REVIEW: "warning",
	SELECTED: "success",
	REJECTED: "error",
};

// Transition
const Transition = forwardRef(function Transition(props, ref) {
	return <Fade ref={ref} {...props} timeout={300} />;
});

// Styled frosted-glass dialog
const BlurredDialog = styled(Dialog)(({ theme }) => ({
	"& .MuiPaper-root": {
		borderRadius: 16,
		border: `2px solid ${theme.palette.primary.main}`,
		background: (theme.vars || theme).palette.background.paper,
		backdropFilter: "blur(8px)",
		boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
		padding: theme.spacing(3),
	},
}));

export default function ShortlistedResumesPage() {
	const { id } = useParams();
	const [results, setResults] = useState([]);
	const [loading, setLoading] = useState(true);
	const location = useLocation();
	const { JobTitle } = location.state || {};
	const [selectedResume, setSelectedResume] = useState(null);

	useEffect(() => {
		const fetchShortlisted = async () => {
			try {
				const body = { jobId: parseInt(id), topN: 5 };
				const response = await getMatchingResumes(body);
				if (response.success) {
					setResults(response.message.results);
				}
			} catch (err) {
				console.error("Error fetching shortlisted resumes:", err);
			} finally {
				setLoading(false);
			}
		};

		fetchShortlisted();
	}, [id]);

	const handleStatusChange = async (appId, newStatus) => {
  try {
    const response = await updateApplicationStatus(appId, newStatus);
    if (response.success) {
      setResults((prev) =>
        prev.map((a) =>
          a.applicationId === response.data.id
            ? { ...a, applicationStatus: response.data.status }
            : a
        )
      );
    }
  } catch (err) {
    console.error("Error updating status:", err);
  }
};


	const handleViewResume = async (jobId) => {
		try {
			const response = await getResumeById(jobId);
			if (response.success) {
				setSelectedResume(response.message.parsedJson);
			}
		} catch (err) {
			console.error("Error fetching resume:", err);
		}
	};

	if (loading) {
		return (
			<Box display="flex" justifyContent="center" mt={4}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<PageContainer
			title="Shortlisted Resumes"
			breadcrumbs={[
				{ title: "Applications", path: "/app/hr/applications" },
				{ title: JobTitle || "Job", path: `/app/hr/applications/job/${id}/applications` },
				{ title: "Shortlisted Resumes", path: `/app/hr/applications/job/${id}/applications/shortlist` }
			]}
		>

			<Stack spacing={2}>
				{results.length === 0 && (
					<Typography textAlign="center" color="text.secondary">
						No shortlisted resumes found.
					</Typography>
				)}

				{results.map((resume) => (
					<Card key={resume.resumeId} sx={{ borderRadius: 1, boxShadow: 2 }}>
						<CardContent>
							<Box display="flex" justifyContent="space-between" alignItems="center">
								<Box>
									<Typography variant="h6">{resume.name}</Typography>
									<Typography variant="body2">{resume.email}</Typography>
									<Typography variant="body2">{resume.location}</Typography>
									<Typography variant="body2">
										Experience: {resume.experienceYears} years
									</Typography>
									<Typography variant="body2">{resume.summary}</Typography>
								</Box>
								<Chip label={`Score: ${resume.score}`} color="success" />
							</Box>

							<Stack direction="row" spacing={1} flexWrap="wrap" mt={2}>
								{resume.matchedSkills.map((skill, i) => (
									<Chip key={i} label={skill} color="primary" size="small" />
								))}
								{resume.missingSkills.length > 0 &&
									resume.missingSkills.map((skill, i) => (
										<Chip key={i} label={skill} color="default" size="small" variant="outlined" />
									))}
							</Stack>
							<Divider sx={{ my: 2 }} />
							<Box mt={2} display="flex" gap={2}>
								<Button
									variant="outlined"
									size="small"
									onClick={() => handleViewResume(resume.resumeId)}
								>
									View Resume
								</Button>
								<Select
									size="small"
									value={resume.applicationStatus}
									onChange={(e) => handleStatusChange(resume.applicationId, e.target.value)}
								>
									{Object.keys(STATUS_COLORS).map((status) => (
										<MenuItem key={status} value={status}>
											{status}
										</MenuItem>
									))}
								</Select>
							</Box>
						</CardContent>
					</Card>
				))}
			</Stack>
			<BlurredDialog
				open={!!selectedResume}
				onClose={() => setSelectedResume(null)}
				maxWidth="md"
				fullWidth
				slots={{ transition: Transition }}
			>
				{selectedResume && (
					<>
						<DialogTitle>
							<Typography variant="h5" fontWeight="bold">
								{selectedResume.name}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{selectedResume.location || "N/A"}
							</Typography>
						</DialogTitle>

						<DialogContent dividers>
							{/* Contact Info */}
							<Stack direction="row" spacing={2} flexWrap="wrap" mb={2}>
								<Typography variant="body2">📧 {selectedResume.email}</Typography>
								<Typography variant="body2">📱 {selectedResume.phone || "N/A"}</Typography>
								{selectedResume.linkedin && (
									<Typography variant="body2">
										🔗 <a href={`https://${selectedResume.linkedin}`} target="_blank" rel="noreferrer">{selectedResume.linkedin}</a>
									</Typography>
								)}
								{selectedResume.github && (
									<Typography variant="body2">
										💻 <a href={`https://${selectedResume.github}`} target="_blank" rel="noreferrer">{selectedResume.github}</a>
									</Typography>
								)}
							</Stack>

							{/* Summary */}
							{selectedResume.summary && (
								<>
									<Typography variant="h6" gutterBottom>Summary</Typography>
									<Typography variant="body2" paragraph>{selectedResume.summary}</Typography>
									<Divider sx={{ my: 2 }} />
								</>
							)}

							{/* Experience */}
							{selectedResume.experience?.length > 0 && (
								<>
									<Typography variant="h6" gutterBottom>Experience</Typography>
									<List dense>
										{selectedResume.experience.map((exp, i) => (
											<ListItem key={i} sx={{ pl: 0 }}>
												<ListItemText
													primary={`${exp.role} @ ${exp.company}`}
													secondary={`${exp.startDate} - ${exp.endDate} • ${exp.description}`}
												/>
											</ListItem>
										))}
									</List>
									<Divider sx={{ my: 2 }} />
								</>
							)}

							{/* Education */}
							{selectedResume.education?.length > 0 && (
								<>
									<Typography variant="h6" gutterBottom>Education</Typography>
									<List dense>
										{selectedResume.education.map((edu, i) => (
											<ListItem key={i} sx={{ pl: 0 }}>
												<ListItemText
													primary={`${edu.degree} - ${edu.institution}`}
													secondary={`${edu.startDate} - ${edu.endDate} | ${edu.fieldOfStudy}`}
												/>
											</ListItem>
										))}
									</List>
									<Divider sx={{ my: 2 }} />
								</>
							)}

							{/* Projects */}
							{selectedResume.projects?.length > 0 && (
								<>
									<Typography variant="h6" gutterBottom>Projects</Typography>
									<List dense>
										{selectedResume.projects.map((proj, i) => (
											<ListItem key={i} sx={{ pl: 0 }}>
												<ListItemText
													primary={proj.name}
													secondary={proj.description}
												/>
											</ListItem>
										))}
									</List>
									<Divider sx={{ my: 2 }} />
								</>
							)}

							{/* Skills */}
							{selectedResume.skills?.length > 0 && (
								<>
									<Typography variant="h6" gutterBottom>Skills</Typography>
									<Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
										{selectedResume.skills.map((skill, i) => (
											<Chip key={i} label={skill} variant="outlined" size="small" />
										))}
									</Stack>
									<Divider sx={{ my: 2 }} />
								</>
							)}

							{/* Certifications */}
							{selectedResume.certifications?.length > 0 && (
								<>
									<Typography variant="h6" gutterBottom>Certifications</Typography>
									<List dense>
										{selectedResume.certifications.map((cert, i) => (
											<ListItem key={i} sx={{ pl: 0 }}>
												<ListItemText primary={cert} />
											</ListItem>
										))}
									</List>
									<Divider sx={{ my: 2 }} />
								</>
							)}

							{/* Languages */}
							{selectedResume.languages?.length > 0 && (
								<>
									<Typography variant="h6" gutterBottom>Languages</Typography>
									<Stack direction="row" spacing={1} flexWrap="wrap">
										{selectedResume.languages.map((lang, i) => (
											<Chip key={i} label={lang} color="primary" size="small" />
										))}
									</Stack>
								</>
							)}
						</DialogContent>

						<DialogActions>
							<Button onClick={() => setSelectedResume(null)} variant="outlined">
								Close
							</Button>
						</DialogActions>
					</>
				)}
			</BlurredDialog>
		</PageContainer>
	);
}
